import type { NextResponse } from 'next/server';
import { parseCookieHeader } from '@/src/lib/cookies/cookieParser';

// hotpads-api has no user photo field, so the Google id_token `picture` claim is
// relayed from the OAuth callback to the browser via this cookie. The value is
// bound to the session's userToken so a different account's login on the same
// browser never renders a stale photo.
export const GOOGLE_AVATAR_COOKIE = 'hp_google_avatar';

const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;

export function isAllowedGoogleAvatarUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname === 'googleusercontent.com' || parsed.hostname.endsWith('.googleusercontent.com'))
    );
  } catch {
    return false;
  }
}

// Clears the cookie when there is no (valid) picture so a photo the user has
// since removed from their Google account doesn't linger for 30 days.
export function syncGoogleAvatarCookie(response: NextResponse, userToken: string, photoUrl: unknown): void {
  if (!userToken || typeof photoUrl !== 'string' || !isAllowedGoogleAvatarUrl(photoUrl)) {
    response.cookies.set(GOOGLE_AVATAR_COOKIE, '', { path: '/', maxAge: 0 });
    return;
  }

  response.cookies.set(GOOGLE_AVATAR_COOKIE, JSON.stringify({ userToken, photoUrl }), {
    httpOnly: false,
    secure: true,
    sameSite: 'lax',
    path: '/',
    maxAge: THIRTY_DAYS_SECONDS,
  });
}

function parseGoogleAvatarCookie(decodedValue: string, userToken: string): string | undefined {
  try {
    const parsed = JSON.parse(decodedValue) as { userToken?: unknown; photoUrl?: unknown };
    if (parsed.userToken !== userToken || typeof parsed.photoUrl !== 'string') return undefined;
    return isAllowedGoogleAvatarUrl(parsed.photoUrl) ? parsed.photoUrl : undefined;
  } catch {
    return undefined;
  }
}

export function googleAvatarUrlFromCookieHeader(
  cookieHeader: string | undefined,
  userToken: string,
): string | undefined {
  if (!cookieHeader || !userToken || !cookieHeader.includes(GOOGLE_AVATAR_COOKIE)) return undefined;

  // decodeValues: ResponseCookies.set percent-encodes the value on write
  const value = parseCookieHeader(cookieHeader, { decodeValues: true })[GOOGLE_AVATAR_COOKIE];
  return value ? parseGoogleAvatarCookie(value, userToken) : undefined;
}

export function getClientGoogleAvatarUrl(userToken: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return googleAvatarUrlFromCookieHeader(document.cookie, userToken);
}

export function clearGoogleAvatarCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${GOOGLE_AVATAR_COOKIE}=; max-age=0; path=/; SameSite=Lax`;
}
